import { useContext } from 'react';
import Link from 'next/link';
import { Box, Paragraph, TextButton, Tooltip, TriggerText } from '@zillow/constellation-10';
import { format } from 'date-fns';
import { useAuth } from '../../../../AuthProvider';
import {
  getLatestMessageDateText,
  isDisplayableTimestamp,
  MESSAGE_LOAD_ERROR_TEXT,
  UnreadIndicator,
} from '@zg-rentals/satellite-components';
import { LeadManagementContext } from '../../../../contexts/LeadManagementContextProvider';
import useLeadManagementActions from '../../../../hooks/useLeadManagementActions';
import useQueryParamsNext from '../../../../hooks/useQueryParamsNext';
import useDevices from '../../../../hooks/useDevices';
import { leadManagementRenterDetailsSectionToScrollToStore } from '../../../../lib/leadManagementStores';
import getLeadManagementMessageText from '../../../../utils/getLeadManagementMessageText';
import type { SatelliteWebLandlordLeadManagementTableRowLatestContactDto } from '@zg-rentals/ts-satellite-client';
import {
  CLICK_ON_LATEST_CONTACT_COLUMN_EVENT_TYPE_ID,
  emitClickOnLatestContactColumnEvent,
} from '../../../../constants/clickstreamEvents/leadManagementTable';

type LatestContactMessageProps = {
  conversationId: string;
  isConversationBlockedByZillow: boolean;
  latestContactDetails: SatelliteWebLandlordLeadManagementTableRowLatestContactDto;
  leadId?: string;
  shouldDisplayTimestampInline?: boolean;
  listingAlias: string;
};

export default function LatestContactMessage({
  conversationId,
  isConversationBlockedByZillow,
  latestContactDetails,
  leadId,
  shouldDisplayTimestampInline = false,
  listingAlias,
}: LatestContactMessageProps) {
  const { isArchivedFolder, isReportedFolder } = useContext(LeadManagementContext);
  const { updateQueryParams } = useQueryParamsNext();
  const { isMdAndBelow } = useDevices();
  const { user } = useAuth();
  const { handleOpenRenterMessagingModal } = useLeadManagementActions({ conversationId });
  const { isMessageOwner, isPhoneLead, isRead, messageDateMs, messageText } = latestContactDetails;
  // The DTO types messageDateMs as required, but the wire value can be absent — and `String(undefined)`
  // is truthy, so it would reach date-fns as NaN and throw.
  const dateMs = String(messageDateMs ?? '');
  const displayableDateMs = isDisplayableTimestamp(dateMs) ? dateMs : '';
  const timestamp = getLatestMessageDateText(displayableDateMs);
  const popoverTimestamp = displayableDateMs ? format(new Date(+displayableDateMs), 'yyyy MMM d h:mm a') : '';
  const messagePrefix = isMessageOwner && messageText ? 'You: ' : '';
  const message = getLeadManagementMessageText({
    isConversationBlockedByZillow,
    messageText,
  });

  const handleMessageTextClick = () => {
    emitClickOnLatestContactColumnEvent(user, { listingAlias }, { leadId });

    if (isMdAndBelow) {
      // Open RenterDetailsModal and scroll to latest contact section
      leadManagementRenterDetailsSectionToScrollToStore.set('latestContact');
      updateQueryParams({ queryParamValues: { conversationId }, shouldReplaceHistory: true });
      return;
    }

    handleOpenRenterMessagingModal();
  };

  const voiceMessageTooltipContent = (
    <Tooltip.Content>
      <Box css={{ marginBlock: 'tighter' }}>
        {messageText ? (
          <audio controls={true} style={{ height: '32px', width: '312px' }}>
            <source src={messageText} />
          </audio>
        ) : (
          <Paragraph>{MESSAGE_LOAD_ERROR_TEXT}</Paragraph>
        )}
        <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
          {popoverTimestamp}
        </Paragraph>
        <Box css={{ paddingTop: 'default' }}>
          <Link
            legacyBehavior
            href={{
              pathname: '/inbox/[[...conversationSlug]]',
              query: {
                conversationSlug: [listingAlias, conversationId],
              },
            }}
            passHref
          >
            <TextButton asChild>
              <a>View in messages</a>
            </TextButton>
          </Link>
        </Box>
      </Box>
      <Tooltip.Arrow />
    </Tooltip.Content>
  );

  return (
    <>
      <Box css={{ display: 'flex', alignItems: 'baseline', flexDirection: 'row', gap: 'tighter' }}>
        {!isRead && (
          <Box css={{ display: 'flex', alignSelf: 'center' }}>
            <UnreadIndicator />
          </Box>
        )}
        {isPhoneLead && !isConversationBlockedByZillow ? (
          <Tooltip.Root placement="bottom-start" shouldAwaitInteractionResponse>
            <Tooltip.Trigger>
              <TriggerText>
                <Paragraph textStyle={isRead ? 'body-sm' : 'body-sm-bold'}>Voice message</Paragraph>
              </TriggerText>
            </Tooltip.Trigger>
            <Tooltip.Portal>{voiceMessageTooltipContent}</Tooltip.Portal>
          </Tooltip.Root>
        ) : (
          <TextButton
            data-eventid={CLICK_ON_LATEST_CONTACT_COLUMN_EVENT_TYPE_ID}
            tone="neutral"
            textStyle={isRead ? 'body-sm' : 'body-sm-bold'}
            onClick={handleMessageTextClick}
            disabled={isArchivedFolder || isReportedFolder}
            css={{ truncate: true, display: 'inline-block', justifyContent: 'flex-start' }}
          >
            {`${messagePrefix}${message}`}
          </TextButton>
        )}

        {shouldDisplayTimestampInline && (
          <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', whiteSpace: 'nowrap' }}>
            {timestamp}
          </Paragraph>
        )}
      </Box>

      {!shouldDisplayTimestampInline && (
        <Paragraph textStyle="body-xs" css={{ marginTop: 'tightest', color: 'text.subtle' }}>
          {timestamp}
        </Paragraph>
      )}
    </>
  );
}
